import { PageHeader, Button, Link } from '@flos/react-ui';
import { useHippoText } from '../../hooks/useHippoText';
import { accountHolderAtom } from '../../recoil/atoms/accountHolderAtom';
import { useRecoilValue } from 'recoil';
import { getHeaderConfig } from './receiptHeaderConfiguration';
import { track } from '../../utils/trackingUtil';
import { useState } from 'react';
import ViewInformationModal from '../../shared_components/ViewInformationModal/ViewInformationModal';
import { sortedCoverablesListSelector } from '../../recoil/selectors/sortedCoverablesListSelector';
import DOMPurify from 'dompurify';
import type { ContainsSpinOffProducts } from '../../recoil/types/daTypes';
import { topConfigContextAtom } from '../../recoil/atoms/topConfigContextAtom';
import { giftCertificateAtom } from '../../recoil/atoms/giftCertificateAtom';
import { jobAtom } from '../../recoil/atoms/jobAtom';
import { fetchedPaymentInformationAtom } from '../../recoil/atoms/fetchedPaymentInformationAtom';
import { isGiftCardEligibleSelector } from '../../recoil/selectors/isGiftCardEligibleSelector';
import { businessFlowSelector } from '../../recoil/selectors/getBusinessFlowSelector';
import { isAllPRHCoverablesWithReportOrUWI } from '../../utils/coverablesUtil';
import { getOPCReceiptPageChangeTypeAtom } from '../../recoil/atoms/getOPCReceiptPageChangeTypeAtom';

const ReceiptHeader = ({
    newInsurances,
    changedInsurances,
    containsSpinOffProducts
}: {
    newInsurances: number;
    changedInsurances: number;
    containsSpinOffProducts: ContainsSpinOffProducts;
}): React.JSX.Element => {
    const giftCoverableMap = useRecoilValue(giftCertificateAtom);
    const { sortedCoverables } = useRecoilValue(sortedCoverablesListSelector);
    const isGiftCardEligible = useRecoilValue(isGiftCardEligibleSelector);
    const businessFlow = useRecoilValue(businessFlowSelector);
    const jobData = useRecoilValue(jobAtom);
    const giftEnabledCoverables = sortedCoverables.filter(
        (coverable) =>
            isGiftCardEligible &&
            coverable.flavourCode &&
            giftCoverableMap[coverable.flavourCode] &&
            coverable.isAddedInOnsa &&
            !coverable.willSpinOff &&
            businessFlow !== 'OPC' &&
            !jobData.isOpcJob
    );
    const { isWinbackOffer } = useRecoilValue(jobAtom);
    const { isExistingCustomer } = useRecoilValue(accountHolderAtom);
    const getHippoText = useHippoText();
    const [showModal, setShowModal] = useState<boolean>(false);
    const topConfigContext = useRecoilValue(topConfigContextAtom);
    const { paymentMethod = '', paymentMethodOnIssuance } = useRecoilValue(
        fetchedPaymentInformationAtom
    );
    const mitTopDKHostURL = topConfigContext?.siteConfig?.scope?.includes('mit.')
        ? ''
        : `//${topConfigContext?.siteConfig?.sisterHost ?? ''}`;
    const getOPCReceiptPageChangeType = useRecoilValue(getOPCReceiptPageChangeTypeAtom);
    const { title, bulletPoints, buttonText, subTitle } = getHeaderConfig(
        isWinbackOffer,
        paymentMethod,
        paymentMethodOnIssuance,
        newInsurances,
        changedInsurances,
        isExistingCustomer,
        getHippoText,
        getOPCReceiptPageChangeType,
        sortedCoverables
    );
    const viewInformationModalGiftCertificate = {
        title: getHippoText('DSS-GIFT-MODAL-TITLE'),
        rawHtmlDescription: getHippoText('DSS-GIFT-MODAL-DESCRIPTION-RECEIPT'),
        description: ''
    };

    return (
        <>
            <PageHeader
                title={title}
                imageUrl={`${topConfigContext?.resourceUrl ?? ''}/images/insurance-2.svg`}
                subtitle={subTitle ?? getHippoText('DSS-RECEIPT-PAGE-DEFAULT-SUBHEADER')}
                style={{ marginLeft: -16, marginRight: -16 }} // Only way to counteract the padding. Ulrich approved.
            >
                <div>
                    {containsSpinOffProducts !== 'OnlySpinOff' && (
                        <ul>
                            {bulletPoints.map((text, id) => (
                                <li key={id}>{text}</li>
                            ))}
                        </ul>
                    )}
                    {giftEnabledCoverables.length > 0 && (
                        <ul>
                            <li>
                                {getHippoText('DSS-RECEIPT-PAGE-GIFT')}{' '}
                                <Link
                                    onClick={() => {
                                        setShowModal(true);
                                    }}
                                >
                                    Læs mere her
                                </Link>
                            </li>
                        </ul>
                    )}
                    {containsSpinOffProducts !== 'NoSpinOff' && (
                        <div
                            dangerouslySetInnerHTML={{
                                __html: DOMPurify.sanitize(
                                    getHippoText('DSS-RECEIPT-PAGE-SPIN-OFF-SUBHEADER')
                                )
                            }}
                        ></div>
                    )}
                    {containsSpinOffProducts !== 'OnlySpinOff' &&
                        !isAllPRHCoverablesWithReportOrUWI(sortedCoverables) && (
                            <Button
                                onClick={() => {
                                    track(`Clicked ${buttonText} on receipt page`, [
                                        {
                                            ReceiptPage: `Customer has clicked "${buttonText}"`
                                        }
                                    ]);

                                    window.location.href = `${mitTopDKHostURL}/forsikringer/`;
                                }}
                            >
                                {buttonText}
                            </Button>
                        )}
                </div>
                <ViewInformationModal
                    showModal={showModal}
                    modalProps={viewInformationModalGiftCertificate}
                    closeModal={() => {
                        setShowModal(false);
                    }}
                />
            </PageHeader>
        </>
    );
};
export default ReceiptHeader;
